import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Download, Search, Eye, ChevronLeft, ChevronRight, RefreshCw, Upload, Mail, CheckCircle, AlertTriangle, Scale } from "lucide-react";
import TransactionDetailView from "../components/transaction-detail-view";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { api } from "../lib/api";
import { useToast } from "@/hooks/use-toast";
import { normalizeWeightToTons } from "../lib/utils";
import { Checkbox } from "@/components/ui/checkbox";

export default function ExternalWeighbridge() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [operatorFilter, setOperatorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [selectedForValidation, setSelectedForValidation] = useState<Set<number>>(new Set());
  const [validationFilter, setValidationFilter] = useState<"all" | "matched" | "discrepancy">("all");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();


  const { data: sessions, isLoading, refetch } = useQuery({
    queryKey: ["/api/sessions/all-mines"],
    queryFn: async () => {
      const response = await fetch("/api/sessions/all-mines", {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-Id': localStorage.getItem('tenant_id') || '',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return api.getSessions();
        }
        throw new Error('Failed to fetch sessions from all mines');
      }
      
      return response.json();
    },
  });

  const allTransactions = (sessions || []).map((session: any) => ({
    id: session.id,
    transactionNo: session.id?.toString() || "N/A",
    date: session.createdAt ? new Date(session.createdAt).toLocaleDateString() : "N/A",
    time: session.createdAt ? new Date(session.createdAt).toLocaleTimeString() : "N/A",
    orderNo: session.orderExternalId || session.clientOrderNumber || "N/A",
    ticketNo: session.ticketNo || "N/A",
    transactionType: session.entryMethod === "manual" ? "MANUAL" : "AUTO",
    
    // Vehicle information
    vehicleReg: session.vehiclePlateNumber || session.plateNumber || "N/A",
    trailer1PlateNumber: session.trailer1PlateNumber,
    trailer2PlateNumber: session.trailer2PlateNumber,
    
    driverName: session.driverName || "Unknown",
    driverId: session.driverId,
    tareWeight: parseFloat(session.tareWeight || "0"),
    grossWeight: parseFloat(session.grossWeight || "0"),
    netWeight: parseFloat(session.netWeight || "0"),
    unit: session.unit || "kg",
    weighbridgeName: session.weighbridgeName || "Main Scale",
    customerName: session.customerName,
    freightCompany: session.customerName,
    transporterName: session.supplier || session.transporterName,
    tenantName: session.tenantName || session.tenantCode || "Unknown Mine",
    
    entryTime: session.entryTime,
    exitTime: session.exitTime,
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    shift: session.shift,
    operator: session.operator,
    
    duration: session.endedAt && session.startedAt ? 
      Math.round((new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 60000) + " min" : "N/A",
    
    status: session.status || "active",
    visitStatus: session.visitStatus,
    kanbanStatus: session.kanbanStatus,
    accessDecision: session.accessDecision,
    
    supplierExternalId: session.supplierExternalId,
    sealNumber: session.sealNumber,
    weighbridgeEntryWeight: session.weighbridgeEntryWeight,
    weighbridgeExitWeight: session.weighbridgeExitWeight,
    deliveryPointEntryWeight: session.deliveryPointEntryWeight,
    deliveryPointExitWeight: session.deliveryPointExitWeight
  }));

  const allFilteredTransactions = allTransactions.filter(transaction => {
    if (searchTerm && 
        !transaction.transactionNo.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !transaction.vehicleReg.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.driverName.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.orderNo.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.tenantName.toLowerCase().includes(searchTerm.toLowerCase())
    ) return false;
    
    if (operatorFilter !== "all" && transaction.operator !== operatorFilter) return false;
    
    if (statusFilter !== "all" && transaction.status !== statusFilter) return false;
    
    return true;
  });

  const totalItems = allFilteredTransactions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredTransactions = allFilteredTransactions.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, operatorFilter, statusFilter]);

  const operators = Array.from(new Set(allTransactions.map(t => t.operator))).filter(op => op && op !== "Unknown");

  const handleRefresh = async () => {
    await refetch();
    toast({
      title: "Refreshed",
      description: "External weighbridge data refreshed successfully",
    });
  };

  // Calculate weight discrepancy between mine (source) and delivery point (external)
  const calculateDiscrepancy = (transaction: any) => {
    const mineWeight = parseFloat(transaction.weighbridgeExitWeight || transaction.netWeight || 0);
    const externalWeight = parseFloat(transaction.deliveryPointEntryWeight || 0);
    
    if (mineWeight > 0 && externalWeight > 0) {
      const difference = mineWeight - externalWeight;
      const percentDiff = ((difference / mineWeight) * 100);
      return {
        mineWeight,
        externalWeight, 
        difference,
        percentDiff,
        hasDiscrepancy: Math.abs(percentDiff) > 1 // Flag if more than 1% difference
      };
    }
    return null;
  };

  // Export to Excel
  const handleExportExcel = () => {
    const headers = ['Date', 'Mine', 'Order No', 'Vehicle', 'Driver', 'Tare (tons)', 'Gross (tons)', 'Net (tons)', 'Mine Weight', 'External Weight', 'Difference', 'Status'];
    const rows = allFilteredTransactions.map(t => {
      const disc = calculateDiscrepancy(t);
      return [
        t.date,
        t.tenantName,
        t.orderNo,
        t.vehicleReg,
        t.driverName,
        normalizeWeightToTons(t.tareWeight),
        normalizeWeightToTons(t.grossWeight),
        normalizeWeightToTons(t.netWeight),
        disc ? normalizeWeightToTons(disc.mineWeight) : '-',
        disc ? normalizeWeightToTons(disc.externalWeight) : '-',
        disc ? `${disc.difference.toFixed(0)} kg (${disc.percentDiff.toFixed(1)}%)` : '-',
        t.status
      ].join(',');
    });
    
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `external-weighbridge-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({ title: "Exported", description: "Data exported to Excel successfully" });
  };

  // Export PDF Report
  const handleExportReport = async () => {
    toast({ title: "Generating Report", description: "PDF report is being generated..." });
    // Simulate report generation
    setTimeout(() => {
      toast({ title: "Report Ready", description: "Your report has been generated" });
    }, 2000);
  };

  // Email Report
  const handleEmailReport = () => {
    toast({ title: "Email", description: "Report will be emailed to registered addresses" });
  };

  // Upload file
  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      toast({ title: "File Selected", description: `${file.name} selected for upload` });
    }
  };

  // Toggle selection for validation
  const toggleValidationSelection = (id: number) => {
    const newSelection = new Set(selectedForValidation);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedForValidation(newSelection);
  };

  // Get transactions with discrepancy info
  const transactionsWithDiscrepancy = allFilteredTransactions.map(t => ({
    ...t,
    discrepancy: calculateDiscrepancy(t)
  }));

  // Filter by validation status
  const getFilteredByValidation = () => {
    if (validationFilter === "all") return transactionsWithDiscrepancy;
    if (validationFilter === "matched") return transactionsWithDiscrepancy.filter(t => t.discrepancy && !t.discrepancy.hasDiscrepancy);
    if (validationFilter === "discrepancy") return transactionsWithDiscrepancy.filter(t => t.discrepancy && t.discrepancy.hasDiscrepancy);
    return transactionsWithDiscrepancy;
  };

  // Stats for validation
  const discrepancyStats = {
    total: transactionsWithDiscrepancy.filter(t => t.discrepancy).length,
    matched: transactionsWithDiscrepancy.filter(t => t.discrepancy && !t.discrepancy.hasDiscrepancy).length,
    discrepancies: transactionsWithDiscrepancy.filter(t => t.discrepancy && t.discrepancy.hasDiscrepancy).length
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading external weighbridge transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Hidden file input for upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".csv,.xlsx,.xls"
      />

      {/* Transaction History Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-[hsl(187,100%,42%)]" />
            <div>
              <h1 className="text-2xl font-bold text-[hsl(187,100%,42%)]">Transaction History</h1>
              <p className="text-sm text-muted-foreground">
                Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} transactions
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={handleUpload} variant="outline" size="sm" data-testid="button-upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
            <Button onClick={handleExportReport} variant="outline" size="sm" data-testid="button-export-report">
              <FileText className="w-4 h-4 mr-2" />
              Export Report
            </Button>
            <Button onClick={handleExportExcel} variant="outline" size="sm" data-testid="button-export-excel">
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button onClick={handleEmailReport} variant="outline" size="sm" className="border-[hsl(187,100%,42%)] text-[hsl(187,100%,42%)]" data-testid="button-email-report">
              <Mail className="w-4 h-4 mr-2" />
              Email Report
            </Button>
            <Button onClick={() => setShowValidateDialog(true)} variant="default" size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="button-validate">
              <Scale className="w-4 h-4 mr-2" />
              Validate Weights
            </Button>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex flex-1 items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="input-search-transaction"
              />
            </div>
            
            <Select value={operatorFilter} onValueChange={setOperatorFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Operators" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Operators</SelectItem>
                {operators.map(operator => (
                  <SelectItem key={operator} value={operator}>{operator}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>External Weighbridge Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Mine</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order Number</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Transporter</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Number Plate</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Driver Fullname</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">ID</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tare</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Gross</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Net</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredTransactions.map((transaction) => {
                    return (
                    <tr key={transaction.id} className="hover:bg-muted/30">
                      <td className="px-3 py-3">
                        <div className="text-sm">
                          {transaction.startedAt ? new Date(transaction.startedAt).toLocaleDateString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleDateString() : transaction.date)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {transaction.endedAt ? new Date(transaction.endedAt).toLocaleTimeString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleTimeString() : transaction.time)}
                        </div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <Badge variant="outline">{transaction.tenantName}</Badge>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm font-mono">{transaction.orderNo || '-'}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm">{transaction.transporterName || '-'}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="font-medium text-sm" data-testid={`vehicle-${transaction.id}`}>
                          {transaction.vehicleReg}
                        </div>
                        {transaction.trailer1PlateNumber && (
                          <div className="text-xs text-muted-foreground">T1: {transaction.trailer1PlateNumber}</div>
                        )}
                        {transaction.trailer2PlateNumber && (
                          <div className="text-xs text-muted-foreground">T2: {transaction.trailer2PlateNumber}</div>
                        )}
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm font-medium">{transaction.driverName}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm font-mono">{transaction.driverId || '-'}</div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <span className="text-sm font-medium">
                          {transaction.tareWeight ? `${normalizeWeightToTons(transaction.tareWeight)} tons` : '-'}
                        </span>
                      </td>
                      
                      <td className="px-3 py-3">
                        <span className="text-sm font-medium">
                          {transaction.grossWeight ? `${normalizeWeightToTons(transaction.grossWeight)} tons` : '-'}
                        </span>
                      </td>
                      
                      <td className="px-3 py-3">
                        <span className="font-bold text-green-600">
                          {transaction.netWeight ? `${normalizeWeightToTons(transaction.netWeight)} tons` : '-'}
                        </span>
                      </td>
                      
                      <td className="px-3 py-3">
                        <Dialog open={isDetailViewOpen && selectedTransaction?.id === transaction.id} onOpenChange={(open) => {
                          if (!open) {
                            setIsDetailViewOpen(false);
                            setSelectedTransaction(null);
                          }
                        }}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setIsDetailViewOpen(true);
                              }}
                              data-testid={`button-view-details-${transaction.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Transaction Details & Images</DialogTitle>
                            </DialogHeader>
                            {selectedTransaction && (
                              <TransactionDetailView transaction={selectedTransaction} />
                            )}
                          </DialogContent>
                        </Dialog>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No transactions found matching your search criteria.</p>
              <p className="text-sm mt-2">Try adjusting your filters or search terms.</p>
            </div>
          )}
  
          {totalItems > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Show</span>
                <Select 
                  value={itemsPerPage.toString()} 
                  onValueChange={(value) => {
                    setItemsPerPage(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>per page</span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  data-testid="button-previous-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {(() => {
                    const pages = [];
                    const showPages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                    let endPage = Math.min(totalPages, startPage + showPages - 1);
                    
                    if (endPage - startPage < showPages - 1) {
                      startPage = Math.max(1, endPage - showPages + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <Button
                          key={1}
                          variant={1 === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          className="w-8 h-8 p-0"
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="ellipsis1" className="px-2">...</span>);
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          variant={i === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(i)}
                          className="w-8 h-8 p-0"
                          data-testid={`button-page-${i}`}
                        >
                          {i}
                        </Button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="ellipsis2" className="px-2">...</span>);
                      }
                      pages.push(
                        <Button
                          key={totalPages}
                          variant={totalPages === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          className="w-8 h-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="button-next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validate Weights Dialog */}
      <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-[hsl(187,100%,42%)]" />
              Weight Validation - Compare Internal vs External
            </DialogTitle>
            <DialogDescription>
              Compare weighbridge weights (internal/mine) against delivery point weights (external) to identify discrepancies
            </DialogDescription>
          </DialogHeader>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 my-4">
            <Card className="bg-muted/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-[hsl(187,100%,42%)]">{discrepancyStats.total}</div>
                <div className="text-sm text-muted-foreground">Total Comparable</div>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-500">{discrepancyStats.matched}</div>
                <div className="text-sm text-muted-foreground">Matched (Within 1%)</div>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-500">{discrepancyStats.discrepancies}</div>
                <div className="text-sm text-muted-foreground">Discrepancies</div>
              </CardContent>
            </Card>
          </div>

          {/* Filter buttons */}
          <div className="flex items-center gap-2 mb-4">
            <Button 
              variant={validationFilter === "all" ? "default" : "outline"} 
              size="sm"
              onClick={() => setValidationFilter("all")}
            >
              All
            </Button>
            <Button 
              variant={validationFilter === "matched" ? "default" : "outline"} 
              size="sm"
              onClick={() => setValidationFilter("matched")}
              className={validationFilter === "matched" ? "bg-green-500 hover:bg-green-600" : ""}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Matched
            </Button>
            <Button 
              variant={validationFilter === "discrepancy" ? "default" : "outline"} 
              size="sm"
              onClick={() => setValidationFilter("discrepancy")}
              className={validationFilter === "discrepancy" ? "bg-red-500 hover:bg-red-600" : ""}
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Discrepancies
            </Button>
          </div>

          {/* Validation Table */}
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Select</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Order</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Vehicle</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Mine Weight</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">External Weight</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Difference</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {getFilteredByValidation().map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <Checkbox 
                        checked={selectedForValidation.has(transaction.id)}
                        onCheckedChange={() => toggleValidationSelection(transaction.id)}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm">{transaction.date}</td>
                    <td className="px-3 py-2 text-sm font-mono">{transaction.orderNo}</td>
                    <td className="px-3 py-2 text-sm">{transaction.vehicleReg}</td>
                    <td className="px-3 py-2 text-sm font-medium">
                      {transaction.discrepancy ? `${normalizeWeightToTons(transaction.discrepancy.mineWeight)} tons` : '-'}
                    </td>
                    <td className="px-3 py-2 text-sm font-medium">
                      {transaction.discrepancy ? `${normalizeWeightToTons(transaction.discrepancy.externalWeight)} tons` : '-'}
                    </td>
                    <td className="px-3 py-2">
                      {transaction.discrepancy ? (
                        <span className={`text-sm font-medium ${transaction.discrepancy.hasDiscrepancy ? 'text-red-500' : 'text-green-500'}`}>
                          {transaction.discrepancy.difference > 0 ? '+' : ''}{transaction.discrepancy.difference.toFixed(0)} kg
                          <span className="text-xs ml-1">({transaction.discrepancy.percentDiff.toFixed(1)}%)</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">N/A</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {transaction.discrepancy ? (
                        transaction.discrepancy.hasDiscrepancy ? (
                          <Badge variant="destructive" className="bg-red-500/20 text-red-400">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Discrepancy
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Matched
                          </Badge>
                        )
                      ) : (
                        <Badge variant="secondary">No Data</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {getFilteredByValidation().length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions with comparable weight data found.</p>
              <p className="text-sm mt-2">Transactions need both internal (mine) and external (delivery point) weights to compare.</p>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {selectedForValidation.size} transactions selected
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowValidateDialog(false);
                  setSelectedForValidation(new Set());
                }}
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  toast({ 
                    title: "Validation Exported", 
                    description: `Exported validation report for ${selectedForValidation.size || discrepancyStats.total} transactions` 
                  });
                }}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Validation Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

